import React, { Fragment, useCallback, useState } from "react";
import { Alert, Spinner, Table } from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import { fetchIncomeStatement } from "../../../api/accounting";
import { formatTZS } from "../../../utils/format";
import { formatApiError, runLoad } from "../Common/credoraToast";
import AccountingReportFilters from "./AccountingReportFilters";
import { ACCOUNTING_SECTION } from "./accountingPlaceholders";
import { accountingReportBreadcrumbs } from "./accountingReportShell";
import { useAccountingReportFilters } from "./useAccountingReportFilters";

const IncomeStatementPage = ({ reportMode = false }) => {
  const crumbs = accountingReportBreadcrumbs({ reportMode, title: "Income Statement (P&L)" });
  const filters = useAccountingReportFilters();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    return runLoad(() => fetchIncomeStatement(filters.reportParams()).then(setData), {
      errorMessage: "Failed to load income statement.",
    })
      .catch((err) => setError(formatApiError(err, "Failed to load income statement.")))
      .finally(() => setLoading(false));
  }, [filters.reportParams]);

  const renderSection = (section) => (
    <>
      <tr className="table-light">
        <td colSpan={2}><strong>{section.title}</strong></td>
        <td className="text-end"><strong>{formatTZS(section.total)}</strong></td>
      </tr>
      {section.lines.map((line) => (
        <tr key={line.account_id}>
          <td className="ps-4">{line.account_code}</td>
          <td>{line.account_name}</td>
          <td className="text-end">{formatTZS(line.amount)}</td>
        </tr>
      ))}
    </>
  );

  return (
    <Fragment>
      <Breadcrumbs mainTitle={crumbs.mainTitle} parent={crumbs.parent} title={crumbs.title} />
      <div className={`container-fluid${reportMode ? " credora-finance-reports pb-5 mb-3" : ""}`}>
        {error && <Alert color="danger">{error}</Alert>}
        <AccountingReportFilters {...filters} onApply={load} loading={loading} />
        {loading ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : data ? (
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Profit & loss</h5>
              <small className="text-muted">
                {data.period_name || `${data.date_from} – ${data.date_to}`}
                {data.branch_name ? ` · ${data.branch_name}` : ""}
              </small>
            </div>
            <div className="card-body">
              <Table responsive bordered size="sm">
                <thead><tr><th>Code</th><th>Account</th><th className="text-end">Amount</th></tr></thead>
                <tbody>
                  {renderSection(data.revenue)}
                  {renderSection(data.expenses)}
                  <tr className="table-success">
                    <td colSpan={2}><strong>Net income</strong></td>
                    <td className="text-end"><strong>{formatTZS(data.net_income)}</strong></td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </div>
        ) : null}
      </div>
    </Fragment>
  );
};

export default IncomeStatementPage;
